import { GridFilterDefinitions, GridFilterEntry, IGridFilter, IGridFilterDefinition } from "@gtmhub/shared/components/grid-filtering-bar/grid-filters/models";
import { localize } from "@gtmhub/localization";
import { toGridDefinitionMap } from "@gtmhub/shared/components/grid-filtering-bar/models";
import { IRestLayerRequest } from "@gtmhub/core/rest-layer";
import { defaultOperatorFactories, RestLayerFilterFactory, toRestLayerRequest } from "@gtmhub/shared/components/grid-filtering-bar/rest-layer-request-builder";
import { IGridSortingRule } from "@gtmhub/shared/components/grid-filtering-bar";
import { capitalize } from "@gtmhub/util";

export const generateDefaultCustomFieldsFilters = (): IGridFilter[] => {
  const filters: IGridFilter[] = [];

  const labelFilter: IGridFilter = {
    fieldName: "label",
    dataType: "text",
    operator: "contains",
    value: "",
  };
  filters.push(labelFilter);

  const targetTypesFilter: IGridFilter = {
    fieldName: "targetTypes",
    dataType: "array",
    operator: "contains",
    value: [],
  };
  filters.push(targetTypesFilter);

  const visualizationKindFilter: IGridFilter = {
    fieldName: "visualizationKind",
    dataType: "array",
    operator: "contains",
    value: [],
  };
  filters.push(visualizationKindFilter);

  return filters;
};

export const customFieldsFilterDefinitions = (): GridFilterDefinitions => {
  const baseDefinitions: IGridFilterDefinition[] = [];

  baseDefinitions.push({
    fieldName: "targetTypes",
    displayName: localize("applied_to"),
    trackingName: "Applied to",
    dataType: "array",
    arrayType: "static",
    placeholder: localize("search_criteria"),
    entries: targetTypesEntries(),
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
  });

  baseDefinitions.push({
    fieldName: "visualizationKind",
    displayName: localize("type"),
    trackingName: "Type",
    dataType: "array",
    arrayType: "static",
    placeholder: localize("search_criteria"),
    entries: visualizationKindsEntries(),
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
  });

  baseDefinitions.push({
    fieldName: "label",
    displayName: localize("label"),
    trackingName: "Label",
    dataType: "text",
    placeholder: localize("search_for_label"),
    defaultOperator: "contains",
    supportedOperators: ["equals", "notEqual", "contains", "doesNotContain"],
    defaultValue: "",
  });

  baseDefinitions.push({
    fieldName: "targetIds",
    displayName: localize("session_s"),
    trackingName: "sessions",
    dataType: "array",
    arrayType: "session",
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
  });

  return toGridDefinitionMap([...baseDefinitions]);
};

export const targetTypesEntries = (): GridFilterEntry[] => [
  {
    displayName: capitalize(localize("objective")),
    value: "goal",
  },
  {
    displayName: localize("key_result_cap"),
    value: "metric",
  },
  {
    displayName: localize("key_result_update"),
    value: "metric_snapshot",
  },
  {
    displayName: localize("task"),
    value: "task",
  },
  {
    displayName: localize("user_cap"),
    value: "user",
  },
  {
    displayName: localize("team_cap"),
    value: "team",
  },
];

export const visualizationKindsEntries = (): GridFilterEntry[] => [
  {
    displayName: localize("text_field"),
    value: "gtmhub.text",
  },
  {
    displayName: localize("text_area"),
    value: "gtmhub.textarea",
  },
  {
    displayName: localize("drop_down_menu"),
    value: "gtmhub.select",
  },
  {
    displayName: localize("multi_select_list"),
    value: "gtmhub.multiselect",
  },
  {
    displayName: localize("date_picker"),
    value: "gtmhub.datepicker",
  },
  {
    displayName: localize("user_team_selector"),
    value: "gtmhub.selectassignee",
  },
];

export const initializeCustomFieldsRequest = (filters: IGridFilter[], sortingRules: IGridSortingRule[]): IRestLayerRequest => {
  const filtersOverride: Record<string, RestLayerFilterFactory> = {
    targetIds: (filter) => {
      const defaultFactory = defaultOperatorFactories[filter.operator];
      const operationalTypeFilter: IGridFilter = { fieldName: "operationalType", dataType: "text", operator: "notEqual", value: "session" };
      const targetTypesFilter: IGridFilter = { fieldName: "targetTypes", dataType: "array", operator: "contains", value: ["goal", "metric", "metric_snapshot"] };
      const operationalTypeFactory = defaultOperatorFactories["notEqual"];

      return {
        ["$or"]: [
          defaultFactory({ ...filter, fieldName: "targetIds" }),
          {
            ["$and"]: [
              operationalTypeFactory({ ...operationalTypeFilter, fieldName: "operationalType" }),
              defaultFactory({ ...targetTypesFilter, fieldName: "targetTypes" }),
            ],
          },
        ],
      };
    },
  };

  return toRestLayerRequest({ filters: filters, filtersOverride: filtersOverride, sorting: sortingRules });
};
